import { PortfolioService } from '@services/portfolio/PortfolioService';
import { theme } from '@theme';
import { SuccessToast } from '@utils/ToastUtils';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePortfolioBulkActionsContainer } from '../../../../../hooks/UsePortfolioBulkActionsContainer';
import { AppState } from '../../../../../store/AppState';
import { setIsPortfolioLoading } from '../../../../../store/portfolio/PortfolioActions';
import { PortfolioState } from '../../../../../store/portfolio/PortfolioReducer';
import { FilterItemResource } from '../../../../../types/FilterTypes';
import { ReassignDealsResource } from '../../../../../types/PortfolioBulkActionResource';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import { PortfolioSalesPerson } from '../../../../../types/SalesPerson';
import ButtonComponent from '../../../../shared/button/ButtonComponent';
import MultiSelectDropdown from '../../../../shared/filters/multi-select-dropdown/MultiSelectDropdown';
import ConfirmationModal from '../../../../shared/modals/confirmation-modal/ConfirmationModal';
import { Typography } from '../../../../style';

type ReassignDealsProps = {
  isVisible: boolean;
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  selectedRecordsCount: number;
  selectedRecords: PortfolioResource[];
  toggleSelectedRows: (value: boolean) => void;
};

const ReassignDeals: FC<ReassignDealsProps> = (props) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [assignees, setAssignees] = useState<PortfolioSalesPerson[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<FilterItemResource[]>([]);

  const dispatch = useDispatch();
  const { isLoading, dealType } = useSelector<AppState, PortfolioState>((state) => state.portfolio);
  const { createReassignPayload, bulkReassignDeals } = usePortfolioBulkActionsContainer();

  const handleSubmit = (): void => {
    let payload: ReassignDealsResource = createReassignPayload(
      assignees,
      selectedAssignees,
      props.isAllRowsSelected,
      props.isClearSelectionLink,
      props.selectedRecords,
    );
    const successCallback = () => {
      props.toggleSelectedRows(false);
      setSelectedAssignees([]);
      if (props.isAllRowsSelected && props.isClearSelectionLink) {
        SuccessToast('Processing reassigned deals');
      } else {
        SuccessToast('Successfully reassigned ' + props.selectedRecordsCount + ' deals');
      }
    };

    setConfirmModalOpen(false);

    bulkReassignDeals(payload, successCallback);
  };

  const handleReassign = (): void => {
    if (props.isAllRowsSelected && props.isClearSelectionLink) {
      setConfirmModalOpen(true);
    } else {
      handleSubmit();
    }
  };

  const handleCancel = (): void => {
    setConfirmModalOpen(false);
  };

  const bodyComponent: JSX.Element = (
    <div className='reassign-deals-modal-body'>
      <Typography
        size={theme.typography.h4}
        display='block'
        marginTop={15}
        fontWeight={theme.typography.fontWeights.semibold}
      >
        Before submitting, please ensure that all necessary changes have been made. Your predictions
        will be live within a few minutes of submission. You will receive an email when updates are
        complete.
      </Typography>
      <Typography size={theme.typography.h4} display='block' marginTop={10}>
        To be included in the predictive campaigns, please submit your changes by the communicated
        deadline.
      </Typography>
      <Typography size={theme.typography.h4} display='block' marginTop={10}>
        Contact your Dealer Relations Manager for more information.
      </Typography>
    </div>
  );

  const updateAssigneeOptions: FilterItemResource[] = assignees.map((x) => {
    const item: FilterItemResource = {
      label: x.name,
      key: x.name,
      count: x.dealCount,
    };
    return item;
  });

  useEffect(() => {
    if (props.isVisible) {
      if (isLoading) {
        return;
      }

      dispatch(setIsPortfolioLoading(true));
      PortfolioService.fetchPortfolioAssignee(dealType)
        .then((res) => {
          setAssignees(res);
        })
        .catch((err) => {
          console.log('Error in loading portfolio assignees', err);
        })
        .finally(() => {
          dispatch(setIsPortfolioLoading(false));
        });
    }
  }, [props.isVisible]);

  return (
    <>
      <MultiSelectDropdown
        placeholder={'Reassign Deals'}
        allowSearchFilter={true}
        enableCheckAll={true}
        values={selectedAssignees}
        onChange={setSelectedAssignees}
        displaySelectedLabels={false}
        type='am-dropdown'
        options={updateAssigneeOptions}
        isSearchOnlyText={true}
        dynamicRender={false}
      >
        <ButtonComponent
          color={theme.palette.white}
          disabled={selectedAssignees.length < 1}
          backgroundColor={
            selectedAssignees.length > 0 ? theme.palette.purple : theme.palette.disabledGrey
          }
          text={'Reassign'}
          onClick={handleReassign}
          justifyContent='center'
        />
      </MultiSelectDropdown>
      <ConfirmationModal
        cancelButtonText='Cancel'
        submitButtonText='Reassign Deals'
        handleCancelClick={handleCancel}
        handleSubmitClick={handleSubmit}
        title='Confirm Reassign Deals'
        isOpen={confirmModalOpen}
        bodyComponent={bodyComponent}
      />
    </>
  );
};

export default ReassignDeals;
